<template>
  <div class="test-details">
    <!-- Summary Cards -->
    <div class="row mb-4">
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card stats-card">
          <div class="card-body text-center">
            <feather-icon icon="FileTextIcon" size="24" class="text-primary mb-2" />
            <h4 class="mb-1">{{ testData.length || 0 }}</h4>
            <small class="text-muted">Всего тестов</small>
          </div>
        </div>
      </div>
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card stats-card">
          <div class="card-body text-center">
            <feather-icon icon="TrendingUpIcon" size="24" class="text-success mb-2" />
            <h4 class="mb-1">{{ getAverageScore() }}%</h4>
            <small class="text-muted">Средний балл</small>
          </div>
        </div>
      </div>
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card stats-card">
          <div class="card-body text-center">
            <feather-icon icon="AwardIcon" size="24" class="text-warning mb-2" />
            <h4 class="mb-1">{{ getBestScore() }}%</h4>
            <small class="text-muted">Лучший результат</small>
          </div>
        </div>
      </div>
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card stats-card">
          <div class="card-body text-center">
            <feather-icon icon="ClockIcon" size="24" class="text-info mb-2" />
            <h4 class="mb-1">{{ getAverageTime() }}</h4>
            <small class="text-muted">Среднее время</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Subject Performance -->
    <div class="row mb-4">
      <div class="col-lg-8 mb-4">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">Результаты по предметам</h5>
          </div>
          <div class="card-body">
            <div v-if="subjectPerformance.length > 0">
              <div
                v-for="subject in subjectPerformance"
                :key="subject.subject"
                class="subject-item mb-3"
              >
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h6 class="mb-0">{{ subject.subject }}</h6>
                  <span class="text-muted">{{ subject.test_count }} тест{{ getTestWordEnding(subject.test_count) }}</span>
                </div>
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <span>Средний балл:</span>
                  <span :class="getScoreClass(subject.average_score)">{{ subject.average_score }}%</span>
                </div>
                <b-progress
                  :value="subject.average_score"
                  :variant="getProgressVariant(subject.average_score)"
                  height="8px"
                />
              </div>
            </div>
            <div v-else class="text-center py-3 text-muted">
              <p>Данные по предметам отсутствуют</p>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-4 mb-4">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">Распределение оценок</h5>
          </div>
          <div class="card-body">
            <canvas ref="gradeChart" width="200" height="200"></canvas>
            <div class="grade-stats mt-3">
              <div v-for="(count, grade) in gradeDistribution" :key="grade" class="d-flex justify-content-between">
                <span>{{ grade }}:</span>
                <span>{{ count }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tests List -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Список тестов</h5>
            <div class="d-flex align-items-center">
              <!-- Subject Filter -->
              <b-form-select
                v-model="subjectFilter"
                :options="subjectOptions"
                size="sm"
                class="mr-2"
                style="width: auto;"
              />
              <!-- Search -->
              <b-input-group size="sm" style="width: 200px;">
                <b-form-input
                  v-model="searchQuery"
                  placeholder="Поиск тестов..."
                />
                <b-input-group-append>
                  <b-button variant="outline-secondary" size="sm">
                    <feather-icon icon="SearchIcon" size="14" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </div>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="thead-light">
                  <tr>
                    <th>Тест</th>
                    <th>Предмет</th>
                    <th>Дата</th>
                    <th>Длительность</th>
                    <th>Вопросов</th>
                    <th>Балл</th>
                    <th>Оценка</th>
                    <th>Статус</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="test in paginatedTests"
                    :key="test.id"
                    class="test-row"
                  >
                    <td>
                      <div>
                        <strong>{{ test.test_title }}</strong>
                        <br>
                        <small class="text-muted">{{ getTestTypeText(test.test_type) }}</small>
                      </div>
                    </td>
                    <td>{{ test.subject || 'Общий' }}</td>
                    <td>{{ formatDate(test.taken_at) }}</td>
                    <td>{{ formatDuration(test.duration_minutes) }}</td>
                    <td>
                      <span class="text-success">{{ test.questions_correct }}</span>/{{ test.questions_total }}
                    </td>
                    <td>
                      <span :class="getScoreClass(test.score)">{{ test.score }}%</span>
                    </td>
                    <td>
                      <b-badge v-if="test.grade" :variant="getGradeVariant(test.grade)">
                        {{ test.grade }}
                      </b-badge>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <b-badge :variant="getStatusVariant(test.status)">
                        {{ getStatusText(test.status) }}
                      </b-badge>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- No Results -->
            <div v-if="filteredTests.length === 0" class="text-center py-5">
              <feather-icon icon="FileTextIcon" size="48" class="text-muted mb-3" />
              <h5 class="text-muted">Тесты не найдены</h5>
              <p class="text-muted">Попробуйте изменить фильтры поиска</p>
            </div>

            <!-- Pagination -->
            <div v-if="filteredTests.length > itemsPerPage" class="p-3 border-top">
              <b-pagination
                v-model="currentPage"
                :total-rows="filteredTests.length"
                :per-page="itemsPerPage"
                align="center"
                size="sm"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BBadge,
  BProgress,
  BFormSelect,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BButton,
  BPagination
} from 'bootstrap-vue'

export default {
  name: 'TestDetails',
  components: {
    BBadge,
    BProgress,
    BFormSelect,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BButton,
    BPagination
  },
  props: {
    testData: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      subjectFilter: 'all',
      searchQuery: '',
      currentPage: 1,
      itemsPerPage: 10
    }
  },
  computed: {
    subjectPerformance() {
      const subjects = {}
      this.testData.forEach(test => {
        const subject = test.subject || 'Общий'
        if (!subjects[subject]) {
          subjects[subject] = { scores: [], count: 0 }
        }
        subjects[subject].scores.push(test.score)
        subjects[subject].count++
      })

      return Object.entries(subjects).map(([subject, data]) => ({
        subject,
        average_score: Math.round(data.scores.reduce((sum, score) => sum + score, 0) / data.count),
        test_count: data.count
      }))
    },
    gradeDistribution() {
      const distribution = {}
      this.testData.forEach(test => {
        if (test.grade) {
          distribution[test.grade] = (distribution[test.grade] || 0) + 1
        }
      })
      return distribution
    },
    subjectOptions() {
      const subjects = ['all']
      this.testData.forEach(test => {
        const subject = test.subject || 'Общий'
        if (!subjects.includes(subject)) {
          subjects.push(subject)
        }
      })
      return subjects.map(subject => ({
        value: subject === 'all' ? 'all' : subject,
        text: subject === 'all' ? 'Все предметы' : subject
      }))
    },
    filteredTests() {
      let filtered = [...this.testData]

      if (this.subjectFilter !== 'all') {
        filtered = filtered.filter(test => (test.subject || 'Общий') === this.subjectFilter)
      }

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(test =>
          test.test_title.toLowerCase().includes(query) ||
          (test.subject && test.subject.toLowerCase().includes(query))
        )
      }

      return filtered.sort((a, b) => new Date(b.taken_at) - new Date(a.taken_at))
    },
    paginatedTests() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      return this.filteredTests.slice(start, start + this.itemsPerPage)
    }
  },
  methods: {
    getAverageScore() {
      if (this.testData.length === 0) return '0.0'
      const average = this.testData.reduce((sum, test) => sum + test.score, 0) / this.testData.length
      return average.toFixed(1)
    },
    getBestScore() {
      if (this.testData.length === 0) return '0.0'
      return Math.max(...this.testData.map(test => test.score)).toFixed(1)
    },
    getAverageTime() {
      if (this.testData.length === 0) return '-'
      const total = this.testData.reduce((sum, test) => sum + (test.duration_minutes || 0), 0)
      const average = total / this.testData.length
      return this.formatDuration(Math.round(average))
    },
    getScoreClass(score) {
      if (score >= 90) return 'text-success font-weight-bold'
      if (score >= 80) return 'text-primary font-weight-bold'
      if (score >= 70) return 'text-info'
      if (score >= 60) return 'text-warning'
      return 'text-danger'
    },
    getProgressVariant(score) {
      if (score >= 90) return 'success'
      if (score >= 70) return 'primary'
      if (score >= 50) return 'warning'
      return 'danger'
    },
    getGradeVariant(grade) {
      const excellent = ['A+', 'A', 'A-']
      const good = ['B+', 'B', 'B-']
      const satisfactory = ['C+', 'C', 'C-']

      if (excellent.includes(grade)) return 'success'
      if (good.includes(grade)) return 'primary'
      if (satisfactory.includes(grade)) return 'info'
      if (grade === 'D') return 'warning'
      return 'danger'
    },
    getStatusVariant(status) {
      const variants = {
        completed: 'success',
        in_progress: 'warning',
        timed_out: 'danger',
        abandoned: 'secondary'
      }
      return variants[status] || 'secondary'
    },
    getStatusText(status) {
      const texts = {
        completed: 'Завершен',
        in_progress: 'В процессе',
        timed_out: 'Время вышло',
        abandoned: 'Прерван'
      }
      return texts[status] || status
    },
    getTestTypeText(type) {
      const types = {
        quiz: 'Викторина',
        exam: 'Экзамен',
        control: 'Контрольная',
        assessment: 'Оценочный'
      }
      return types[type] || type
    },
    getTestWordEnding(count) {
      if (count === 1) return ''
      if (count >= 2 && count <= 4) return 'а'
      return 'ов'
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    },
    formatDuration(minutes) {
      if (!minutes) return '-'
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60

      if (hours > 0) {
        return mins > 0 ? `${hours}ч ${mins}м` : `${hours}ч`
      }
      return `${mins}м`
    },
    createGradeChart() {
      const canvas = this.$refs.gradeChart
      if (!canvas || Object.keys(this.gradeDistribution).length === 0) return

      const ctx = canvas.getContext('2d')
      const distribution = this.gradeDistribution

      const centerX = canvas.width / 2
      const centerY = canvas.height / 2
      const radius = Math.min(centerX, centerY) - 10

      let startAngle = -Math.PI / 2
      const total = Object.values(distribution).reduce((sum, count) => sum + count, 0)
      const colors = ['#28a745', '#007bff', '#17a2b8', '#ffc107', '#dc3545']

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      Object.entries(distribution).forEach(([grade, count], index) => {
        const sliceAngle = (count / total) * 2 * Math.PI

        ctx.beginPath()
        ctx.arc(centerX, centerY, radius, startAngle, startAngle + sliceAngle)
        ctx.lineTo(centerX, centerY)
        ctx.fillStyle = colors[index % colors.length]
        ctx.fill()

        startAngle += sliceAngle
      })
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.createGradeChart()
    })
  },
  watch: {
    testData: {
      handler() {
        this.$nextTick(() => {
          this.createGradeChart()
        })
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.test-details {
  .stats-card {
    border: none;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-2px);
    }
  }

  .subject-item {
    padding: 1rem;
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
    background: #f8f9fa;

    &:hover {
      background: #e9ecef;
    }
  }

  .grade-stats {
    font-size: 0.9rem;
  }

  .table .test-row:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  .thead-light th {
    background-color: #f8f9fa;
    font-weight: 600;
    color: #6c757d;
    border-top: none;
  }
}

.card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.dark-layout {
  .test-details {
    .stats-card,
    .card {
      background-color: #283046;
      color: #d0d2d6;
    }

    .subject-item {
      background-color: #404656;
      border-color: #555;

      &:hover {
        background-color: #4a5568;
      }
    }

    .thead-light th {
      background-color: #404656;
      color: #b4b7bd;
      border-color: #555;
    }

    .table {
      color: #d0d2d6;

      .test-row:hover {
        background-color: rgba(255, 255, 255, 0.05);
      }

      td {
        border-color: #555;
      }
    }

    .text-muted {
      color: #b4b7bd !important;
    }
  }
}
</style>